/**
 * Utility functions for API calls with rate limit handling
 */

interface RetryConfig {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
};

/**
 * Sleep for a specified number of milliseconds
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay with jitter
 */
const getRetryDelay = (attempt: number, baseDelay: number, maxDelay: number): number => {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // Add 0-30% jitter
  return Math.min(exponentialDelay + jitter, maxDelay);
};

/**
 * Fetch with automatic retry on rate limit errors (429)
 */
export const fetchWithRetry = async (
  url: string,
  options: RequestInit = {},
  retryConfig: RetryConfig = {}
): Promise<Response> => {
  const config = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // If rate limited (429), retry with exponential backoff
      if (response.status === 429 && attempt < config.maxRetries) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter
          ? parseInt(retryAfter) * 1000
          : getRetryDelay(attempt, config.baseDelay, config.maxDelay);

        console.warn(
          `[API] Rate limited (429). Retrying in ${Math.round(delay / 1000)}s (attempt ${attempt + 1}/${config.maxRetries})`
        );

        await sleep(delay);
        continue;
      }

      // Return response for all other status codes (including non-429 errors)
      return response;
    } catch (error) {
      lastError = error as Error;
      
      // Network errors - retry with backoff
      if (attempt < config.maxRetries) {
        const delay = getRetryDelay(attempt, config.baseDelay, config.maxDelay);
        console.warn(
          `[API] Network error. Retrying in ${Math.round(delay / 1000)}s (attempt ${attempt + 1}/${config.maxRetries})`
        );
        await sleep(delay);
        continue;
      }
    }
  }

  throw lastError || new Error('Failed to fetch after retries');
};

/**
 * Make an API call with automatic JSON parsing and retry logic
 */
export const apiCall = async <T = any>(
  endpoint: string,
  options: RequestInit = {},
  retryConfig?: RetryConfig
): Promise<T> => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const url = `${apiUrl}${endpoint}`;

  const response = await fetchWithRetry(url, options, retryConfig);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API call failed: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Helper for POST requests with JSON body
 */
export const apiPost = async <T = any>(
  endpoint: string,
  body: any,
  retryConfig?: RetryConfig
): Promise<T> => {
  return apiCall<T>(
    endpoint,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    retryConfig
  );
};

